import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ValidationSummary = ({ 
  validationResults = {
    total: 0,
    valid: 0,
    warnings: 0,
    errors: 0,
    details: []
  },
  onViewDetails,
  onFixErrors
}) => {
  // FIXED: Extract correct values with proper validation
  const { total, valid, warnings, errors, details } = validationResults;
  
  // FIXED: Calculate valid count if missing (fallback)
  const validCount = typeof valid === 'number' ? valid : Math.max(0, (total || 0) - (errors || 0));
  const warningCount = warnings || 0;
  const errorCount = errors || 0;
  const totalProducts = total || 0;
  
  const getStatusColor = () => {
    if (errorCount > 0) return 'error';
    if (warningCount > 0) return 'warning';
    return 'success';
  };

  const statusColor = getStatusColor();

  const toneStyles = {
    success: {
      headerBg: 'bg-success/5 dark:bg-success/15',
      iconBg: 'bg-success/10 dark:bg-success/20'
    },
    warning: {
      headerBg: 'bg-warning/5 dark:bg-warning/15',
      iconBg: 'bg-warning/10 dark:bg-warning/20'
    },
    error: {
      headerBg: 'bg-error/5 dark:bg-error/15',
      iconBg: 'bg-error/10 dark:bg-error/20'
    }
  };

  const currentTone = toneStyles[statusColor] || toneStyles.success;

  const validationCategories = [
    {
      type: 'title',
      label: 'Títulos',
      icon: 'Type',
      issues: details?.filter(d => d?.field === 'title')
    },
    {
      type: 'price',
      label: 'Precios',
      icon: 'DollarSign',
      issues: details?.filter(d => d?.field === 'price')
    },
    {
      type: 'category',
      label: 'Categorías',
      icon: 'Tag',
      issues: details?.filter(d => d?.field === 'category')
    },
    {
      type: 'description',
      label: 'Descripciones',
      icon: 'FileText',
      issues: details?.filter(d => d?.field === 'description')
    },
    {
      type: 'images',
      label: 'Imágenes',
      icon: 'Image',
      issues: details?.filter(d => d?.field === 'images')
    }
  ];

  if (totalProducts === 0) {
    return null;
  }

  return (
    <div className="bg-surface rounded-lg border border-border">
      {/* Header */}
      <div className={`p-6 border-b border-border ${currentTone?.headerBg}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 ${currentTone?.iconBg} rounded-lg flex items-center justify-center`}>
              <Icon 
                name={errorCount > 0 ? "AlertCircle" : warningCount > 0 ? "AlertTriangle" : "CheckCircle"} 
                size={20} 
                className={`text-${statusColor}`}
              />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">
                Resumen de Validación
              </h3>
              <p className="text-text-secondary">
                {totalProducts} productos analizados
                {totalProducts >= 1000 && (
                  <span className="ml-2 bg-accent/10 text-accent px-2 py-1 rounded text-xs">
                    Alto volumen
                  </span>
                )}
              </p>
            </div>
          </div>
          
          {errorCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              iconName="Wrench"
              iconPosition="left"
              onClick={onFixErrors}
            >
              Corregir Errores
            </Button>
          )}
        </div>
      </div>
      {/* Statistics */}
      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-4 bg-success/10 dark:bg-success/20 rounded-lg border border-success/20 dark:border-success/40">
            <div className="text-2xl font-bold text-success mb-1">{validCount}</div>
            <div className="text-sm text-text-secondary">Válidos</div>
          </div>
          
          <div className="text-center p-4 bg-warning/10 dark:bg-warning/20 rounded-lg border border-warning/20 dark:border-warning/40">
            <div className="text-2xl font-bold text-warning mb-1">{warningCount}</div>
            <div className="text-sm text-text-secondary">Advertencias</div>
          </div>
          
          <div className="text-center p-4 bg-error/10 dark:bg-error/20 rounded-lg border border-error/20 dark:border-error/40">
            <div className="text-2xl font-bold text-error mb-1">{errorCount}</div>
            <div className="text-sm text-text-secondary">Errores</div>
          </div>
          
          <div className="text-center p-4 bg-primary/10 dark:bg-primary/20 rounded-lg border border-primary/20 dark:border-primary/40">
            <div className="text-2xl font-bold text-primary mb-1">
              {totalProducts > 0 ? Math.round((validCount / totalProducts) * 100) : 0}%
            </div>
            <div className="text-sm text-text-secondary">Éxito</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-text-primary">Estado General</span>
            <span className="text-sm text-text-secondary">
              {validCount} de {totalProducts} productos válidos
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-3">
            <div className="flex h-3 rounded-full overflow-hidden">
              <div 
                className="bg-success transition-all duration-300"
                style={{ width: `${totalProducts > 0 ? (validCount / totalProducts) * 100 : 0}%` }}
              ></div>
              <div 
                className="bg-warning transition-all duration-300"
                style={{ width: `${totalProducts > 0 ? (warningCount / totalProducts) * 100 : 0}%` }}
              ></div>
              <div 
                className="bg-error transition-all duration-300"
                style={{ width: `${totalProducts > 0 ? (errorCount / totalProducts) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Enhanced info for high volume */}
        {totalProducts >= 1000 && (
          <div className="mb-6 p-4 bg-accent/10 dark:bg-accent/20 rounded-lg border border-accent/20 dark:border-accent/30">
            <div className="flex items-center space-x-2">
              <Icon name="Zap" className="text-accent" size={16} />
              <div>
                <h4 className="font-medium text-accent">Procesamiento de Alto Volumen</h4>
                <p className="text-sm text-text-secondary mt-1">
                  {totalProducts} productos procesados exitosamente. Optimizado para cargas masivas de hasta 10,000 productos.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Validation Categories */}
        <div className="space-y-4">
          <h4 className="font-medium text-text-primary">Detalles por Categoría</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {validationCategories?.map((category) => {
              const categoryErrors = category?.issues?.filter(i => i?.severity === 'error')?.length || 0;
              const categoryWarnings = category?.issues?.filter(i => i?.severity === 'warning')?.length || 0;
              const hasIssues = categoryErrors > 0 || categoryWarnings > 0;

              return (
                <div 
                  key={category?.type}
                  className={`p-4 rounded-lg border ${
                    categoryErrors > 0 
                      ? 'border-error/20 bg-error/5 dark:border-error/40 dark:bg-error/15' 
                      : categoryWarnings > 0 
                      ? 'border-warning/20 bg-warning/5 dark:border-warning/40 dark:bg-warning/15' :'border-success/20 bg-success/5 dark:border-success/40 dark:bg-success/15'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon name={category?.icon} size={16} className="text-text-secondary" />
                      <span className="font-medium text-text-primary">{category?.label}</span>
                    </div>
                    {hasIssues && (
                      <div className="flex items-center space-x-2 text-sm">
                        {categoryErrors > 0 && (
                          <span className="text-error">{categoryErrors} errores</span>
                        )}
                        {categoryWarnings > 0 && (
                          <span className="text-warning">{categoryWarnings} advertencias</span>
                        )}
                      </div>
                    )}
                  </div>
                  {!hasIssues && (
                    <div className="flex items-center space-x-1 text-success text-sm">
                      <Icon name="CheckCircle" size={14} />
                      <span>Sin problemas detectados</span>
                    </div>
                  )}
                  {hasIssues && category?.issues?.slice(0, 2)?.map((issue, index) => (
                    <div key={index} className="text-sm text-text-secondary mb-1">
                      • {issue?.message}
                    </div>
                  ))}
                  {category?.issues?.length > 2 && (
                    <button
                      onClick={() => onViewDetails?.(category?.type)}
                      className="text-sm text-primary hover:underline"
                    >
                      Ver {category?.issues?.length - 2} más...
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-6 border-t border-border mt-6">
          <Button
            variant="outline"
            iconName="Eye"
            iconPosition="left"
            onClick={() => onViewDetails?.('all')}
            fullWidth
          >
            Ver Detalles Completos
          </Button>
          
          {errorCount === 0 && (
            <Button
              variant="success"
              iconName="CheckCircle"
              iconPosition="left"
              fullWidth
            >
              {totalProducts >= 1000 ? `Listo para Publicar ${totalProducts} Productos` : 'Listo para Publicar'}
            </Button>
          )}
          
          {totalProducts >= 2000 && (
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={() => {}}
              fullWidth
            >
              Exportar Resumen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValidationSummary;